<template>
  <BaseModal
    :is-open="isOpen"
    title="Welcome Back!"
    max-width="550px"
    :closable="false"
  >
    <div class="recovery-container">
      <!-- Status Message -->
      <div class="status-message">
        <span class="material-symbols-outlined status-icon">refresh</span>
        <p>Your game session has been restored.</p>
      </div>

      <!-- Missing Players Section -->
      <div v-if="absentPlayers.length > 0" class="missing-players">
        <h4 class="section-title">
          <span class="material-symbols-outlined">person_off</span>
          Missing Players ({{ absentPlayers.length }})
        </h4>
        <div class="players-list">
          <div
            v-for="player in absentPlayers"
            :key="player.id"
            class="player-item"
          >
            <div class="player-avatar">
              <span class="material-symbols-outlined">person</span>
            </div>
            <div class="player-details">
              <span class="player-name">{{ player.display_name || player.username }}</span>
              <div class="player-meta">
                <span class="seat-badge">
                  <span class="material-symbols-outlined">event_seat</span>
                  Seat {{ player.seat + 1 }}
                </span>
                <span class="team-badge" :class="player.team === 'A' ? 'team-a' : 'team-b'">
                  Team {{ player.team }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Missing Players -->
      <div v-else class="no-missing">
        <span class="material-symbols-outlined">check_circle</span>
        <p>All players are connected!</p>
      </div>

      <!-- Action Section (Creator Only) -->
      <div v-if="isCreator && absentPlayers.length > 0" class="action-section">
        <p class="action-prompt">What would you like to do?</p>
        <div class="recovery-actions">
          <button @click="handleWait" class="btn-wait">
            <span class="material-symbols-outlined">hourglass_empty</span>
            <span>Wait for Players</span>
          </button>
          <button @click="handleReplace" class="btn-replace">
            <span class="material-symbols-outlined">smart_toy</span>
            <span>Replace with Bots</span>
          </button>
        </div>
        <p class="help-text">
          Choose "Wait" to keep spots open, or "Replace" to fill with AI and continue playing.
        </p>
      </div>

      <!-- Non-Creator Waiting -->
      <div v-else-if="!isCreator && absentPlayers.length > 0" class="waiting-section">
        <div class="waiting-indicator">
          <span class="material-symbols-outlined spinning">sync</span>
          <span>Waiting for room leader to decide...</span>
        </div>
      </div>

      <!-- Continue Button (No Missing Players) -->
      <div v-else class="continue-section">
        <button @click="handleContinue" class="btn-continue">
          <span class="material-symbols-outlined">play_arrow</span>
          <span>Continue Game</span>
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseModal from './BaseModal.vue'

/**
 * StaleGameRecoveryModal Component
 *
 * Displayed when a player rejoins a stale game (one with no active connections).
 * Allows the room creator to decide whether to wait for other players or
 * replace absent humans with bots.
 *
 * Props:
 * - isOpen: Whether the modal is visible
 * - data: Recovery data from the server
 *
 * Emits:
 * - action: { action: 'wait' | 'replace_with_bots' | 'continue' }
 */

interface AbsentPlayer {
  id: string
  username: string
  display_name?: string
  seat: number
  team: string
}

interface RecoveryData {
  room_code: string
  game_state: any
  your_player_id: string
  is_creator: boolean
  absent_players: AbsentPlayer[]
  connected_players?: string[]
  message?: string
}

interface Props {
  isOpen: boolean
  data: RecoveryData | null
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  data: null,
})

const emit = defineEmits<{
  action: [{ action: 'wait' | 'replace_with_bots' | 'continue' }]
}>()

/**
 * Whether current user is the room creator
 */
const isCreator = computed(() => props.data?.is_creator ?? false)

/**
 * List of absent players
 */
const absentPlayers = computed(() => props.data?.absent_players ?? [])

/**
 * Handle "Wait for Players" action
 */
function handleWait(): void {
  emit('action', { action: 'wait' })
}

/**
 * Handle "Replace with Bots" action
 */
function handleReplace(): void {
  emit('action', { action: 'replace_with_bots' })
}

/**
 * Handle continue (no missing players)
 */
function handleContinue(): void {
  emit('action', { action: 'continue' })
}
</script>

<style scoped>
.recovery-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0.5rem;
}

/* Status Message */
.status-message {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: rgba(34, 197, 94, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(34, 197, 94, 0.2);
}

.status-icon {
  font-size: 2rem;
  color: #22c55e;
}

.status-message p {
  margin: 0;
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

/* Section Title */
.section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 1rem 0;
}

.section-title .material-symbols-outlined {
  font-size: 1.25rem;
  color: #fbbf24;
}

/* Missing Players */
.missing-players {
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.players-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.player-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.player-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(251, 191, 36, 0.15);
  flex-shrink: 0;
}

.player-avatar .material-symbols-outlined {
  font-size: 1.5rem;
  color: #fbbf24;
}

.player-details {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;
}

.player-name {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.player-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.seat-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
}

.seat-badge .material-symbols-outlined {
  font-size: 0.875rem;
}

.team-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.team-badge.team-a {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.team-badge.team-b {
  background: rgba(59, 130, 246, 0.15);
  color: #60a5fa;
}

/* No Missing Players */
.no-missing {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem;
  background: rgba(34, 197, 94, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(34, 197, 94, 0.2);
}

.no-missing .material-symbols-outlined {
  font-size: 1.5rem;
  color: #22c55e;
}

.no-missing p {
  margin: 0;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
}

/* Action Section */
.action-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.action-prompt {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
}

.recovery-actions {
  display: flex;
  gap: 1rem;
}

.btn-wait,
.btn-replace {
  flex: 1;
  padding: 0.875rem 1.25rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
}

.btn-wait {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.btn-wait:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
}

.btn-replace {
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  color: #0d1f2d;
  box-shadow: 0 4px 12px rgba(232, 197, 71, 0.3);
}

.btn-replace:hover {
  background: linear-gradient(135deg, #f0d060 0%, #e0bf47 100%);
  box-shadow: 0 6px 16px rgba(232, 197, 71, 0.4);
  transform: translateY(-2px);
}

.btn-wait:active,
.btn-replace:active {
  transform: translateY(0);
}

.btn-wait .material-symbols-outlined,
.btn-replace .material-symbols-outlined {
  font-size: 1.25rem;
}

/* Waiting Section */
.waiting-section {
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.waiting-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
}

.spinning {
  animation: spin 1.5s linear infinite;
  color: #e8c547;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Continue Section */
.continue-section {
  display: flex;
  justify-content: center;
}

.btn-continue {
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
}

.btn-continue:hover {
  background: linear-gradient(135deg, #2dd468 0%, #1eb850 100%);
  box-shadow: 0 6px 16px rgba(34, 197, 94, 0.4);
  transform: translateY(-2px);
}

.btn-continue:active {
  transform: translateY(0);
}

.btn-continue .material-symbols-outlined {
  font-size: 1.5rem;
}

/* Help Text */
.help-text {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  margin: 0;
  font-style: italic;
}

/* Responsive */
@media (max-width: 640px) {
  .recovery-actions {
    flex-direction: column;
  }

  .player-item {
    flex-direction: column;
    text-align: center;
  }

  .player-details {
    align-items: center;
  }

  .player-meta {
    justify-content: center;
  }
}
</style>
